// doctor-frontend/src/clinical/medicationDecisionSupport.js
// Safe medication decision-support foundation.
// The legacy medicine list contains human-readable dose references only.
// Weight-based calculations are enabled ONLY when a medicine has an explicit,
// reviewed structured doseRules[] entry. Free-text dose strings are never parsed
// into an automatic recommendation.

function numberValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function getVisitWeightKg(visit = {}) {
  const candidates = [
    visit?.examination?.vitals?.weightKg,
    visit?.examination?.anthropometry?.weight,
    visit?.examination?.generalExam?.weightKg,
    visit?.childWeight,
  ];
  for (const candidate of candidates) {
    const n = numberValue(candidate);
    if (n != null && n > 0 && n < 500) return n;
  }
  return null;
}

function round(value, digits = 1) {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function calculateRule(rule, weightKg) {
  if (!rule || !weightKg) return null;
  const min = numberValue(rule.minMgPerKg);
  const max = numberValue(rule.maxMgPerKg ?? rule.minMgPerKg);
  if (min == null || max == null) return null;

  const basis = String(rule.basis || '').trim();
  let minMg = min * weightKg;
  let maxMg = max * weightKg;

  if (basis === 'mg_per_kg_per_day') {
    const dosesPerDay = numberValue(rule.dosesPerDay);
    if (!dosesPerDay || dosesPerDay <= 0) return null;
    minMg /= dosesPerDay;
    maxMg /= dosesPerDay;
  } else if (basis !== 'mg_per_kg_per_dose') {
    return null;
  }

  const cap = numberValue(rule.maxMgPerDose);
  if (cap != null) {
    minMg = Math.min(minMg, cap);
    maxMg = Math.min(maxMg, cap);
  }

  const concentration = numberValue(rule.formulationMgPerMl);
  return {
    minMgPerDose: round(minMg),
    maxMgPerDose: round(maxMg),
    minMlPerDose: concentration ? round(minMg / concentration, 2) : null,
    maxMlPerDose: concentration ? round(maxMg / concentration, 2) : null,
    frequency: rule.frequency || '',
    duration: rule.duration || '',
    sourceId: rule.sourceId || '',
    sourceVersion: rule.sourceVersion || '',
    reviewed: rule.reviewed === true,
  };
}

export function buildMedicationSupport(medicine = {}, { weightKg = null } = {}) {
  const rules = Array.isArray(medicine?.doseRules) ? medicine.doseRules : [];
  const reviewedRule = rules.find((rule) => rule?.reviewed === true);
  if (!reviewedRule) {
    return {
      status: 'reference-only',
      weightKg,
      adultReference: medicine?.adultDose || '',
      paediatricReference: medicine?.pediatricDose || '',
      message: 'Reference text only — no reviewed structured dose rule is active for automatic calculation.',
    };
  }
  if (!weightKg) {
    return {
      status: 'weight-required',
      weightKg: null,
      adultReference: medicine?.adultDose || '',
      paediatricReference: medicine?.pediatricDose || '',
      message: 'A valid weight from this Visit is required before a weight-based calculation can be shown.',
    };
  }
  const calculated = calculateRule(reviewedRule, weightKg);
  if (!calculated || calculated.reviewed !== true) {
    return {
      status: 'reference-only',
      weightKg,
      adultReference: medicine?.adultDose || '',
      paediatricReference: medicine?.pediatricDose || '',
      message: 'The available rule is not suitable for an automatic calculation.',
    };
  }
  return { status: 'calculated', weightKg, calculated };
}
